// What is being edited in place — at most ONE thing at a time, and it changes
// only by entering or leaving (docs/claude/selection.md, *Edit in place*):
//  - entering a mode leaves whatever was being edited before, its sub-selection
//    with it, so two modes can never overlap and a corner picked in one room can
//    never turn up selected in the next;
//  - leaving names the kinds it ends (none named: any), so a route out that is
//    only about the plan (Deselect, a tool switch, Esc) never closes a part's
//    drawing editor;
//  - a room's picked corner or wall, and the shape picked in a part's drawing,
//    live IN their mode, so they go with it;
//  - which of the plan's own Select gestures still run while a mode is on is one
//    row per mode (`EDIT_RULES`), so a new mode says it before it compiles; what
//    the mode itself does with a press, a double-click, a right-click, Esc, the
//    panel and the prompt is its row in main.ts's `EDIT_LIFE`.
// DOM-free: ui/core.ts holds the value and reads it back through `state`.
import { unreachable } from '../shared/exhaustive';

/** What is being edited in place. */
export type EditMode =
  /** A placed part's own drawing, in the drawing editor (ui/drawingEditor.ts):
   *  the shape picked in it (`PartPick`). */
  | ({ kind: 'part'; id: string } & PartPick)
  /** The imported plan, selected to move / scale / turn. */
  | { kind: 'plan' }
  /** A room's corners and walls (vertex edit): the corner or the wall picked. */
  | { kind: 'room'; id: string; vertex: number | null; edge: number | null }
  /** A polygon's, a wall's or a revision cloud's vertices. */
  | { kind: 'shape'; id: string }
  /** A background underlay (a PDF, an image) being moved / scaled / turned. */
  | { kind: 'underlay'; id: string | null };
export type EditKind = EditMode['kind'];
type ModeOf<K extends EditKind> = Extract<EditMode, { kind: K }>;

/** What a part's drawing editor has picked: one polygon or one line / wall,
 *  the shape whose vertices are shown, and an edge of the polygon picked. */
export interface PartPick {
  poly: string | null;
  wall: string | null;
  vertices: string | null;
  edge: { id: string; edge: number } | null;
}

/** What entering a mode names: its kind and what it edits. Whatever it picks
 *  inside starts empty. */
export type EditEntry =
  | { kind: 'part'; id: string }
  | { kind: 'plan' }
  | { kind: 'room'; id: string }
  | { kind: 'shape'; id: string }
  | { kind: 'underlay'; id: string | null };

/** Where the mode is held (ui/core.ts). */
export interface EditHolder { now: EditMode | null }

/** The mode, if it is of this kind. */
export function editingOf<K extends EditKind>(h: EditHolder, kind: K): ModeOf<K> | null {
  return h.now?.kind === kind ? (h.now as ModeOf<K>) : null;
}

/** Start editing this — whatever was being edited before is left, its
 *  sub-selection with it. A room and a part are entered with nothing picked. */
export function enterEdit(h: EditHolder, m: EditEntry): void {
  switch (m.kind) {
    case 'part': h.now = { kind: 'part', id: m.id, poly: null, wall: null, vertices: null, edge: null }; return;
    case 'plan': h.now = { kind: 'plan' }; return;
    case 'room': h.now = { kind: 'room', id: m.id, vertex: null, edge: null }; return;
    case 'shape': h.now = { kind: 'shape', id: m.id }; return;
    case 'underlay': h.now = { kind: 'underlay', id: m.id }; return;
    default: unreachable(m);
  }
}

/** Stop editing — kinds given, only if the mode is one of them. */
export function leaveEdit(h: EditHolder, ...kinds: EditKind[]): void {
  if (h.now && (!kinds.length || kinds.includes(h.now.kind))) h.now = null;
}

/** In a room's vertex edit, pick a corner (the wall picked is let go); null
 *  picks nothing. Outside one, nothing happens. */
export function pickRoomCorner(h: EditHolder, vertex: number | null): void {
  if (h.now?.kind === 'room') h.now = { ...h.now, vertex, edge: null };
}

/** In a room's vertex edit, pick a wall (the corner picked is let go); null
 *  picks nothing. Outside one, nothing happens. */
export function pickRoomWall(h: EditHolder, edge: number | null): void {
  if (h.now?.kind === 'room') h.now = { ...h.now, edge, vertex: null };
}

/** In a part's drawing editor, change what is picked (the fields given). Outside
 *  one, nothing happens. */
export function pickInPart(h: EditHolder, pick: Partial<PartPick>): void {
  if (h.now?.kind === 'part') h.now = { ...h.now, ...pick };
}

/** Which of the plan's own Select gestures still run while a mode is on. */
export interface EditRules {
  /** The hover outline of what a click would pick. */
  hover: boolean;
  /** A plain press picks the plan's group or object, ahead of the mode's own press. */
  pick: boolean;
  /** Shift + press toggles a plan object into the selection, or starts an adding marquee. */
  shiftPick: boolean;
  /** A press on a selection of two or more drags it. */
  multiMove: boolean;
  /** The arrow keys nudge the selection. */
  nudge: boolean;
  /** Enter finishes the mode, as Esc does. */
  enterLeaves: boolean;
}

/** One row per mode: a new mode does not compile until it says which of the
 *  plan's gestures it keeps. */
export const EDIT_RULES: Record<EditKind, EditRules> = {
  // The part's own drawing is all there is: the plan behind it takes nothing.
  part: { hover: false, pick: false, shiftPick: false, multiMove: false, nudge: false, enterLeaves: false },
  // The imported plan and an underlay take a press on themselves; a press anywhere
  // else leaves them and picks what is there.
  plan: { hover: false, pick: true, shiftPick: true, multiMove: true, nudge: true, enterLeaves: true },
  // A press in the room moves it or its corners; outside, it leaves the mode.
  room: { hover: false, pick: false, shiftPick: true, multiMove: false, nudge: false, enterLeaves: true },
  shape: { hover: true, pick: false, shiftPick: true, multiMove: false, nudge: true, enterLeaves: true },
  underlay: { hover: false, pick: true, shiftPick: true, multiMove: true, nudge: true, enterLeaves: true },
};

/** Whether this gesture of the plan's runs now (with no mode on, every one does). */
export function editAllows(m: EditMode | null, rule: keyof EditRules): boolean {
  return !m || EDIT_RULES[m.kind][rule];
}
